/**
 * Python Export Utilities
 * Generate Python package files for model export
 */
import JSZip from 'jszip';

/**
 * Sanitize name for Python identifier
 */
export const sanitizePythonName = (name) => {
  if (!name) return 'unknown';
  return String(name)
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^[0-9]/, '_$&')
    .toLowerCase();
};

/**
 * Generate __init__.py for main package
 */
export const generateInitPy = (modelName, hardware) => {
  return `"""
${modelName} - Exported from Optimize Hub
Hardware Target: ${hardware}
Generated: ${new Date().toISOString()}
"""
from .layers import *
from .model import ${sanitizePythonName(modelName).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}Model

__version__ = "1.0.0"
`;
};

/**
 * Generate layers/__init__.py
 */
export const generateLayersInitPy = (layers) => {
  const imports = layers.map((layer, idx) =>
    `from .layer_${idx}_${sanitizePythonName(layer.layer_name || layer.name || `layer_${idx}`)} import *`
  ).join('\n');

  return `"""
Layer implementations
"""
${imports}
`;
};

/**
 * Generate individual layer Python file
 * Preserves exact spacing and syntax from the database
 */
export const generateLayerPy = (layer) => {
  const layerName = layer.layer_name || layer.name || layer.id || 'unknown';
  const layerType = layer.layer_type || layer.type || 'composite';

  // Use current_code (modified) if available, else function_code (baseline)
  const code = layer.current_code || layer.function_code || layer.baseline_code || '';

  if (!code || !code.trim()) {
    console.warn(`No code available for layer: ${layerName}`);
    return `# No code available for layer: ${layerName}\n`;
  }

  // Return JUST the function code with minimal header comment
  // Preserve exact indentation and spacing from database
  return `"""
Layer: ${layerName}
Type: ${layerType}
Parameters: ${layer.parameters || 0}
FLOPs: ${layer.flops || 0}
"""
import torch

${code}
`;
};

/**
 * Generate model.py wrapper
 */
export const generateModelPy = (modelName, layers, hardware) => {
  const className = sanitizePythonName(modelName).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('') + 'Model';

  const layerCalls = layers.map((layer, idx) => {
    const code = layer.current_code || layer.function_code || layer.baseline_code || '';
    const funcName = extractFunctionName(code);
    return funcName ? `        # Layer ${idx}: ${layer.layer_name || layer.name}\n        x = ${funcName}(x)` : '';
  }).filter(Boolean).join('\n');

  return `"""
${modelName} - Optimized Model
Hardware: ${hardware}
Total Layers: ${layers.length}
"""
import torch
import torch.nn as nn
from .layers import *


class ${className}(nn.Module):
    """
    ${modelName} optimized for ${hardware}

    This model was exported from Optimize Hub and contains
    ${layers.length} optimized layer implementations.
    """

    def __init__(self):
        super(${className}, self).__init__()
        # Model layers are implemented as standalone functions
        # in the layers module

    def forward(self, x):
        """
        Forward pass through all layers

        Args:
            x: Input tensor

        Returns:
            Output tensor after all layer transformations
        """
${layerCalls || '        # No layer implementations available\n        return x'}

        return x


def load_model():
    """
    Convenience function to load the model

    Returns:
        Initialized ${className} instance
    """
    model = ${className}()
    model.eval()  # Set to evaluation mode
    return model
`;
};

/**
 * Extract function name from Python code
 */
const extractFunctionName = (code) => {
  if (!code) return null;
  const match = code.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/);
  return match ? match[1] : null;
};

/**
 * Generate metadata.json with IO metadata for each layer
 * This enables re-upload and metadata regeneration
 */
export const generateMetadataJson = (session, model, layers) => {
  const modifiedLayers = layers.filter(l => l.is_modified).length;

  // Build layer metadata array with function names and IO metadata
  const layerMetadata = layers.map((layer, idx) => {
    const code = layer.current_code || layer.function_code || layer.baseline_code || '';
    const functionName = extractFunctionName(code);

    return {
      function: functionName || `layer_${idx}`,
      order: idx,
      layer_name: layer.layer_name || layer.name || `layer_${idx}`,
      layer_type: layer.layer_type || layer.type || 'unknown',
      is_modified: layer.is_modified || false,
      io_metadata: layer.io_metadata || {}
    };
  });

  return JSON.stringify({
    model_name: model.name,
    model_id: model.id,
    session_name: session.session_name,
    session_id: session.id,
    hardware_target: session.hardware_target || model.hardware_target,
    export_date: new Date().toISOString(),
    total_layers: layers.length,
    optimized_layers: modifiedLayers,
    platform: "Optimize Hub",
    version: "1.0",
    layers: layerMetadata  // Include layer metadata for re-upload
  }, null, 2);
};

/**
 * Generate README.md
 */
export const generateReadme = (modelName, sessionName, hardware, layerCount, optimizedCount) => {
  const packageName = sanitizePythonName(modelName);
  const className = packageName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('') + 'Model';

  return `# ${modelName} - Optimized Model Export

**Session:** ${sessionName}
**Hardware Target:** ${hardware}
**Export Date:** ${new Date().toLocaleDateString()}
**Total Layers:** ${layerCount}
**Optimized Layers:** ${optimizedCount}

---

## Overview

This model was exported from **Optimize Hub** and contains optimized implementations
for ${hardware} hardware. The model includes ${layerCount} layers, with ${optimizedCount}
custom optimizations applied.

## Installation

### Requirements
\`\`\`bash
pip install torch
\`\`\`

### Using the Exported Model

1. Extract this ZIP file to your project directory
2. Import and use the model:

\`\`\`python
from ${packageName} import ${className}, load_model

# Option 1: Use the convenience loader
model = load_model()

# Option 2: Instantiate directly
model = ${className}()
model.eval()

# Run inference
import torch
input_tensor = torch.randn(1, 3, 224, 224)  # Adjust shape as needed
output = model(input_tensor)
\`\`\`

## Package Structure

\`\`\`
${packageName}/
├── __init__.py           # Package initialization
├── model.py              # Main model class
├── layers/              # Layer implementations
│   ├── __init__.py
│   ├── layer_*.py       # Individual layer functions
├── metadata.json        # Export metadata
└── README.md           # This file
\`\`\`

## Layer Information

Each layer is implemented as a standalone Python function in the \`layers/\` directory.
You can import and use individual layers if needed:

\`\`\`python
from ${packageName}.layers import *
\`\`\`

## Notes

- All layers have been optimized for **${hardware}** hardware
- ${optimizedCount} out of ${layerCount} layers have custom optimizations applied
- The model is exported in evaluation mode by default
- Make sure to adjust input tensor shapes based on your use case

## Support

For questions or issues with this export, please refer to the Optimize Hub documentation
or contact the platform support team.

---

*Generated by Optimize Hub - The world's fastest models, built together.*
`;
};

/**
 * Create ZIP archive with all files
 */
export const createZipArchive = async (files, modelName, sessionName) => {
  const zip = new JSZip();
  const packageName = sanitizePythonName(modelName);

  // Create package structure
  const packageFolder = zip.folder(packageName);
  const layersFolder = packageFolder.folder('layers');

  // Add all files to ZIP
  for (const file of files) {
    if (file.path.startsWith('layers/')) {
      layersFolder.file(file.path.replace('layers/', ''), file.content);
    } else {
      packageFolder.file(file.path, file.content);
    }
  }

  // Generate ZIP blob
  const blob = await zip.generateAsync({ type: 'blob' });

  // Trigger download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const timestamp = new Date().getTime();
  const safeName = sanitizePythonName(sessionName || 'export');
  a.download = `${packageName}_${safeName}_${timestamp}.zip`;
  a.click();
  URL.revokeObjectURL(url);

  return true;
};

/**
 * Main export function - orchestrates the entire export process
 */
export const exportSessionToPython = async (sessionData, modelData, layers) => {
  try {
    const modelName = modelData.name || 'exported_model';
    const sessionName = sessionData.session_name || 'optimization_session';
    const hardware = sessionData.hardware_target || modelData.hardware_target || 'cpu';

    const files = [];

    // Generate main __init__.py
    files.push({
      path: '__init__.py',
      content: generateInitPy(modelName, hardware)
    });

    // Generate model.py
    files.push({
      path: 'model.py',
      content: generateModelPy(modelName, layers, hardware)
    });

    // Generate layers/__init__.py
    files.push({
      path: 'layers/__init__.py',
      content: generateLayersInitPy(layers)
    });

    // Generate individual layer files
    layers.forEach((layer, idx) => {
      const layerName = sanitizePythonName(layer.layer_name || layer.name || layer.id || `layer_${idx}`);

      files.push({
        path: `layers/layer_${idx}_${layerName}.py`,
        content: generateLayerPy(layer)  // generateLayerPy now extracts code from layer
      });
    });

    // Generate metadata.json
    files.push({
      path: 'metadata.json',
      content: generateMetadataJson(sessionData, modelData, layers)
    });

    // Generate README.md
    const optimizedCount = layers.filter(l => l.is_modified).length;
    files.push({
      path: 'README.md',
      content: generateReadme(modelName, sessionName, hardware, layers.length, optimizedCount)
    });

    // Create and download ZIP
    await createZipArchive(files, modelName, sessionName);

    return { success: true, filesGenerated: files.length };
  } catch (error) {
    console.error('Error exporting to Python:', error);
    throw error;
  }
};
